// Drag-to-reorder wrapper for the form builder. Steps and fields both use it:
// a real element, because CSS transforms are ignored on table rows and a drag
// there never moves.
//
// Built on @dnd-kit, the same library SprayJobCreate uses for its zone and
// chemical lists — including the lesson written up there: drag ids must be
// STABLE and unique, never positional. A positional id ("step-0") is the same
// id at the same index after a reorder, so dnd-kit sees nothing move and
// animates the dropped card back to where it started.
//
// 1. React
import React from 'react';

// 2. Third-party
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// Shared sensor setup, so steps and fields behave identically:
//  - pointer: 6px before a drag starts, so a click on the handle is still a
//    click and not an accidental one-pixel drag;
//  - touch: a 250ms long-press, so scrolling the builder on a tablet does not
//    pick a row up;
//  - keyboard: reorder without a mouse at all.
export const DRAG_SENSOR_OPTIONS = {
  pointer: { activationConstraint: { distance: 6 } },
  touch: { activationConstraint: { delay: 250, tolerance: 5 } },
};

const HANDLE_STYLE = {
  cursor: 'grab',
  color: '#98a2b3',
  // Without this, a touch drag is interpreted as a page scroll and the row
  // never moves.
  touchAction: 'none',
};

/**
 * A draggable block, with the handle handed back through a render prop.
 *
 * Render-prop rather than a handle this component positions itself: a step's
 * handle belongs inside the Collapse panel header, next to the move arrows and
 * the delete icon, where an absolutely-positioned one would land on top of
 * them.
 *
 * @param {string}   id       stable drag id
 * @param {boolean}  compact  tight row (a field) rather than a step block
 * @param {function} children ({ handleProps, isDragging }) => node
 */
export const SortableBlock = ({ id, compact = false, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    opacity: isDragging ? 0.7 : 1,
    // Lift the block being dragged above its neighbours, or it slides under
    // the next one instead of over it.
    zIndex: isDragging ? 2 : 'auto',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    marginBottom: compact ? 0 : 8,
    background: isDragging ? '#fff' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        handleProps: { ...attributes, ...listeners, style: HANDLE_STYLE },
        isDragging,
      })}
    </div>
  );
};
